// components/IEP/TransformationCard.js - UPDATED VERSION
import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  LinearProgress,
  Button,
  IconButton,
  Tooltip,
  Divider,
  Snackbar,
  Alert
} from '@mui/material';
import {
  TrendingUp,
  AttachMoney,
  Group,
  Schedule,
  Bolt,
  Warning,
  CheckCircle,
  Star,
  StarBorder,
  ArrowForward,
  Psychology,
  Add,
  Remove,
  Check
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useIEP } from '../../contexts/IEPContext';

const TransformationCard = ({ transformation, compact = false, onFavoriteToggle }) => {
  const navigate = useNavigate();
  const { addToRoadmap, removeFromRoadmap, isInRoadmap } = useIEP(); // ✅ ADDED
  const [isFavorited, setIsFavorited] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  const inRoadmap = isInRoadmap(transformation.id); // ✅ CHECK IF IN ROADMAP
  
  const handleViewDetails = () => {
    navigate(`/iep/transformations/${transformation.id}`);
  };
  
  const handleAddToRoadmap = async (e) => {
    e.stopPropagation();
    
    try {
      if (inRoadmap) {
        // Remove from roadmap
        const result = removeFromRoadmap(transformation.id);
        if (result?.success) {
          setSnackbar({
            open: true,
            message: 'Removed from roadmap successfully',
            severity: 'info'
          });
        }
      } else {
        // Add to roadmap
        const result = addToRoadmap(transformation);
        if (result?.success) {
          setSnackbar({
            open: true,
            message: 'Added to roadmap successfully!',
            severity: 'success'
          });
        } else if (result?.message) {
          setSnackbar({
            open: true,
            message: result.message,
            severity: 'warning'
          });
        }
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to update roadmap',
        severity: 'error'
      });
    }
  };
  
  const handleFavoriteToggle = (e) => {
    e.stopPropagation();
    setIsFavorited(!isFavorited);
    if (onFavoriteToggle) {
      onFavoriteToggle(transformation.id);
    }
  };
  
  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  if (compact) {
    return (
      <>
        <Card 
          elevation={2}
          sx={{ 
            height: '100%',
            cursor: 'pointer',
            transition: 'all 0.3s',
            border: inRoadmap ? '2px solid #10b981' : 'none', // ✅ HIGHLIGHT IF IN ROADMAP
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: 8
            }
          }}
          onClick={handleViewDetails}
        >
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
              <Typography variant="subtitle1" fontWeight="600">
                {transformation.name}
              </Typography>
              <IconButton 
                size="small"
                onClick={handleFavoriteToggle}
                sx={{ ml: 1 }}
              >
                {isFavorited ? (
                  <Star color="warning" fontSize="small" />
                ) : (
                  <StarBorder fontSize="small" />
                )}
              </IconButton>
            </Box>
            
            <Typography variant="body2" color="text.secondary" paragraph sx={{ fontSize: '0.875rem' }}>
              {transformation.description?.substring(0, 80)}...
            </Typography>
            
            <Box mb={2}>
              <Box display="flex" alignItems="center" mb={1}>
                <Bolt fontSize="small" sx={{ mr: 1, color: 'warning.main' }} />
                <Typography variant="caption">
                  Priority: <strong>{transformation.priority}</strong>
                </Typography>
              </Box>
              <Box display="flex" alignItems="center">
                <Schedule fontSize="small" sx={{ mr: 1, color: 'info.main' }} />
                <Typography variant="caption">
                  Timeline: <strong>{transformation.expectedDuration?.min}-{transformation.expectedDuration?.max} days</strong>
                </Typography>
              </Box>
            </Box>
            
            <Divider sx={{ my: 1.5 }} />
            
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Button 
                size="small" 
                variant={inRoadmap ? "contained" : "outlined"} // ✅ DYNAMIC BUTTON STYLE
                color={inRoadmap ? "success" : "primary"}
                startIcon={inRoadmap ? <Check /> : <Add />}
                onClick={handleAddToRoadmap}
                sx={{ minWidth: 120 }}
              >
                {inRoadmap ? 'In Roadmap' : 'Add to Roadmap'}
              </Button>
              <Box display="flex" gap={0.5}>
                <Chip 
                  label={transformation.category}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>
            </Box>
          </CardContent>
        </Card>
        
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={handleSnackbarClose} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </>
    );
  }
  
  // Full version with enhanced AddToRoadmap functionality
  return (
    <>
      <Card 
        elevation={3}
        sx={{ 
          height: '100%',
          cursor: 'pointer',
          transition: 'all 0.3s',
          border: inRoadmap ? '2px solid #10b981' : 'none',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 8
          }
        }}
        onClick={handleViewDetails}
      >
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box>
              <Typography variant="h6" gutterBottom>
                {transformation.name}
                {inRoadmap && (
                  <Chip 
                    label="In Roadmap"
                    size="small"
                    color="success"
                    sx={{ ml: 1, verticalAlign: 'middle' }}
                    icon={<Check fontSize="small" />}
                  />
                )}
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Chip 
                  label={transformation.category}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                {transformation.priority === 'critical' && (
                  <Chip 
                    label="Critical"
                    size="small"
                    color="error"
                    icon={<Warning fontSize="small" />}
                  />
                )}
              </Box>
            </Box>
            <IconButton 
              size="small"
              onClick={handleFavoriteToggle}
            >
              {isFavorited ? (
                <Star color="warning" />
              ) : (
                <StarBorder />
              )}
            </IconButton>
          </Box>
          
          <Typography variant="body2" color="text.secondary" paragraph>
            {transformation.description}
          </Typography>
          
          {/* Impact Score */}
          {transformation.impactScore && (
            <Box mb={2}>
              <Box display="flex" justifyContent="space-between" mb={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Impact Score
                </Typography>
                <Typography variant="caption" fontWeight="600">
                  {(transformation.impactScore * 100).toFixed(0)}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={transformation.impactScore * 100}
                sx={{ height: 6, borderRadius: 3 }}
                color={
                  transformation.impactScore > 0.8 ? 'success' :
                  transformation.impactScore > 0.5 ? 'warning' : 'error'
                }
              />
            </Box>
          )}
          
          {/* Key Metrics */}
          <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={1} mb={2}>
            <Box display="flex" alignItems="center">
              <Bolt fontSize="small" sx={{ mr: 1, color: 'warning.main' }} />
              <Typography variant="caption">
                Priority: <strong>{transformation.priority}</strong>
              </Typography>
            </Box>
            <Box display="flex" alignItems="center">
              <Schedule fontSize="small" sx={{ mr: 1, color: 'info.main' }} />
              <Typography variant="caption">
                Duration: <strong>{transformation.expectedDuration?.min} days</strong>
              </Typography>
            </Box>
            <Box display="flex" alignItems="center">
              <AttachMoney fontSize="small" sx={{ mr: 1, color: 'success.main' }} />
              <Typography variant="caption">
                Budget: <strong>${(transformation.resourceRequirements?.budget?.min / 1000).toFixed(0)}K</strong>
              </Typography>
            </Box>
            <Box display="flex" alignItems="center">
              <Group fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="caption">
                Team: <strong>{transformation.resourceRequirements?.teamSize?.core}</strong>
              </Typography>
            </Box>
          </Box>
          
          {/* ROI Estimate */}
          {transformation.estimatedROI && (
            <Box mb={2} p={1} bgcolor="success.light" borderRadius={1}>
              <Typography variant="caption" fontWeight="600" color="success.dark">
                Expected ROI: {transformation.estimatedROI}x
              </Typography>
            </Box>
          )}
          
          <Divider sx={{ my: 2 }} />
          
          {/* Actions */}
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Chip 
                label={transformation.complexity}
                size="small"
                color={
                  transformation.complexity === 'high' ? 'error' :
                  transformation.complexity === 'medium' ? 'warning' : 'success'
                }
                sx={{ mr: 1 }}
              />
              {transformation.qStarApproach && (
                <Tooltip title="Q* AI Optimized">
                  <Chip 
                    label="Q*"
                    size="small"
                    color="secondary"
                    icon={<Psychology fontSize="small" />}
                  />
                </Tooltip>
              )}
            </Box>
            <Box display="flex" gap={1}>
              <Button 
                size="small" 
                variant={inRoadmap ? "contained" : "outlined"}
                color={inRoadmap ? "success" : "primary"}
                startIcon={inRoadmap ? <Check /> : <Add />}
                onClick={handleAddToRoadmap}
                sx={{ minWidth: 140 }}
              >
                {inRoadmap ? 'In Roadmap' : 'Add to Roadmap'}
              </Button>
              <Button 
                size="small" 
                variant="contained"
                endIcon={<ArrowForward />}
                onClick={handleViewDetails}
              >
                Details
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TransformationCard;